import { useMemo, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  Box, Card, CardContent, Typography, Grid, TextField, MenuItem,
  Stack, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  CircularProgress, Tabs, Tab, IconButton, Alert,
} from '@mui/material';
import { Receipt, CheckCircle, Refresh, AttachMoney, OpenInNew } from '@mui/icons-material';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import { colors } from '../../theme/theme';
import { countyBillingService, BillingReportResponse, BillingReportRow } from '../../services/county-billing.service';
import { superAdminService } from '../../services/superadmin.service';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useNavigate } from 'react-router-dom';

const fmtKES = (v: string | number | null | undefined) =>
  `KES ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const statusColor: Record<string, 'default' | 'warning' | 'success' | 'error'> = {
  pending: 'warning',
  paid_out: 'success',
  cancelled: 'error',
};

export const BillingReportsPage = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const qc = useQueryClient();

  const [tab, setTab] = useState(0); // 0 = By County, 1 = Settlement rows
  const [countyId, setCountyId] = useState<number | ''>('');
  const [type, setType] = useState<'' | 'service_fee' | 'revenue_share'>('');
  const [status, setStatus] = useState<'' | 'pending' | 'paid_out' | 'cancelled'>('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  const filters = useMemo(() => ({
    county_id: countyId === '' ? undefined : Number(countyId),
    type: type || undefined,
    status: status || undefined,
    period_start: periodStart || undefined,
    period_end: periodEnd || undefined,
  }), [countyId, type, status, periodStart, periodEnd]);

  const reportQuery = useQuery({
    queryKey: ['billing-report', filters],
    queryFn: () => countyBillingService.report(filters),
  });

  const countiesQuery = useQuery({
    queryKey: ['superadmin', 'counties', 'all-for-billing-filter'],
    queryFn: () => superAdminService.getCounties(1),
  });

  const report: BillingReportResponse | undefined = (reportQuery.data as any)?.data;
  const counties: any[] = (countiesQuery.data as any)?.data?.data ?? (countiesQuery.data as any)?.data ?? [];

  // Mark-paid dialog
  const [payRow, setPayRow] = useState<BillingReportRow | null>(null);
  const [payRef, setPayRef] = useState('');
  const [payNote, setPayNote] = useState('');
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));

  const markPaid = useMutation({
    mutationFn: () => countyBillingService.markSettlementPaid(payRow!.county_id, payRow!.id, {
      payout_reference: payRef,
      paid_out_at: paidAt,
      notes: payNote || undefined,
    }),
    onSuccess: () => {
      showSnackbar('Settlement marked as paid', 'success');
      setPayRow(null); setPayRef(''); setPayNote('');
      qc.invalidateQueries({ queryKey: ['billing-report'] });
    },
    onError: (e: any) => showSnackbar(e?.response?.data?.message || 'Failed to mark as paid', 'error'),
  });

  const totals = report?.totals;

  return (
    <AdminLayout>
      <PageHeader
        title="Billing Reports"
        subtitle="Settlement batches across all counties — owed to provider, owed to counties, and paid-out totals"
        breadcrumbs={[
          { label: 'Dashboard', path: '/superadmin/dashboard' },
          { label: 'Reports' },
          { label: 'Billing' },
        ]}
      />

      {/* KPI cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: `linear-gradient(135deg, ${colors.warning.main} 0%, ${colors.warning.dark} 100%)`, color: 'white' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', fontWeight: 600 }}>
                    Counties owe provider
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#fff', mt: 0.5 }}>
                    {fmtKES(totals?.pending_owed_to_provider)}
                  </Typography>
                </Box>
                <Receipt sx={{ fontSize: 32, color: 'rgba(255,255,255,0.4)' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: `linear-gradient(135deg, ${colors.info.main} 0%, ${colors.info.dark} 100%)`, color: 'white' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', fontWeight: 600 }}>
                    Provider owes counties
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#fff', mt: 0.5 }}>
                    {fmtKES(totals?.pending_owed_to_counties)}
                  </Typography>
                </Box>
                <AttachMoney sx={{ fontSize: 32, color: 'rgba(255,255,255,0.4)' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: `linear-gradient(135deg, ${colors.success.main} 0%, ${colors.success.dark} 100%)`, color: 'white' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', fontWeight: 600 }}>
                    Paid to provider
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#fff', mt: 0.5 }}>
                    {fmtKES(totals?.paid_to_provider)}
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 32, color: 'rgba(255,255,255,0.4)' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`, color: 'white' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', fontWeight: 600 }}>
                    Paid to counties
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#fff', mt: 0.5 }}>
                    {fmtKES(totals?.paid_to_counties)}
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 32, color: 'rgba(255,255,255,0.4)' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth size="small" select label="County"
                value={countyId} onChange={(e) => setCountyId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <MenuItem value="">All counties</MenuItem>
                {counties.map((c: any) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField fullWidth size="small" select label="Plan type" value={type} onChange={(e) => setType(e.target.value as any)}>
                <MenuItem value="">All types</MenuItem>
                <MenuItem value="service_fee">Service fee</MenuItem>
                <MenuItem value="revenue_share">Revenue share</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField fullWidth size="small" select label="Status" value={status} onChange={(e) => setStatus(e.target.value as any)}>
                <MenuItem value="">All statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="paid_out">Paid out</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField fullWidth size="small" type="date" label="Period start"
                InputLabelProps={{ shrink: true }} value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField fullWidth size="small" type="date" label="Period end"
                InputLabelProps={{ shrink: true }} value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 1 }}>
              <IconButton onClick={() => reportQuery.refetch()} sx={{ height: 40 }}>
                <Refresh />
              </IconButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* WhatsApp volume — last 30 days, per county. Surfaces channel usage alongside billing
          so superadmin can see which counties drive WhatsApp traffic (relevant for ops + ROI). */}
      {report?.whatsapp_volume_30d && report.whatsapp_volume_30d.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700}>WhatsApp volume — last 30 days</Typography>
              <Chip size="small" label={`${report.whatsapp_volume_30d.reduce((a, b) => a + Number(b.total), 0)} messages`} />
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>County</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Inbound</TableCell>
                    <TableCell align="right">Outbound</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.whatsapp_volume_30d.map((r) => {
                    const countyName = counties.find((c: any) => c.id === r.county_id)?.name || `County #${r.county_id}`;
                    return (
                      <TableRow key={r.county_id} hover>
                        <TableCell>{countyName}</TableCell>
                        <TableCell align="right">{r.total}</TableCell>
                        <TableCell align="right">{r.inbound_count}</TableCell>
                        <TableCell align="right">{r.outbound_count}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Tabs: By County rollup vs settlement rows */}
      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="By County" />
          <Tab label="Settlement Rows" />
        </Tabs>
        <CardContent>
          {reportQuery.isLoading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : !report ? (
            <Alert severity="info">No data for the selected filters.</Alert>
          ) : tab === 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>County</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell align="right">Batches</TableCell>
                    <TableCell align="right">Txns</TableCell>
                    <TableCell align="right">Gross</TableCell>
                    <TableCell align="right">Pending → Provider</TableCell>
                    <TableCell align="right">Pending → County</TableCell>
                    <TableCell align="right">Paid → Provider</TableCell>
                    <TableCell align="right">Paid → County</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.per_county.length === 0 ? (
                    <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>No settlements match the filters.</TableCell></TableRow>
                  ) : report.per_county.map((r) => (
                    <TableRow key={`${r.county_id}-${r.billing_type}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{r.county_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.county_code}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={r.billing_type.replace('_', ' ')}
                          color={r.billing_type === 'service_fee' ? 'info' : 'success'}
                          sx={{ textTransform: 'capitalize', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell align="right">{r.batch_count}</TableCell>
                      <TableCell align="right">{r.transaction_count}</TableCell>
                      <TableCell align="right">{fmtKES(r.gross_collected)}</TableCell>
                      <TableCell align="right">{r.billing_type === 'service_fee' ? fmtKES(r.pending_provider_total) : '—'}</TableCell>
                      <TableCell align="right">{r.billing_type === 'revenue_share' ? fmtKES(r.pending_county_total) : '—'}</TableCell>
                      <TableCell align="right">{r.billing_type === 'service_fee' ? fmtKES(r.paid_provider_total) : '—'}</TableCell>
                      <TableCell align="right">{r.billing_type === 'revenue_share' ? fmtKES(r.paid_county_total) : '—'}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => navigate(`/superadmin/counties/${r.county_id}/billing`)}>
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>County</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell align="right">Txns</TableCell>
                    <TableCell align="right">Gross</TableCell>
                    <TableCell align="right">Provider</TableCell>
                    <TableCell align="right">County</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.rows.data.length === 0 ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No settlements match the filters.</TableCell></TableRow>
                  ) : report.rows.data.map((s: BillingReportRow) => (
                    <TableRow key={s.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{s.county_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.county_code}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={s.billing_type.replace('_', ' ')}
                          color={s.billing_type === 'service_fee' ? 'info' : 'success'}
                          sx={{ textTransform: 'capitalize', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{new Date(s.period_start).toLocaleDateString()} → {new Date(s.period_end).toLocaleDateString()}</Typography>
                      </TableCell>
                      <TableCell align="right">{s.transaction_count}</TableCell>
                      <TableCell align="right">{fmtKES(s.gross_collected)}</TableCell>
                      <TableCell align="right">{fmtKES(s.provider_share_total)}</TableCell>
                      <TableCell align="right">{fmtKES(s.county_share_total)}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={s.status} color={statusColor[s.status] || 'default'} sx={{ fontWeight: 700, textTransform: 'capitalize' }} />
                      </TableCell>
                      <TableCell align="center">
                        {s.status === 'pending' ? (
                          <Button size="small" variant="contained" color="success" onClick={() => { setPayRow(s); setPayRef(''); setPayNote(''); }}>
                            Mark Paid
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {s.payout_reference || '—'}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Mark-paid dialog */}
      <Dialog open={!!payRow} onClose={() => setPayRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark settlement as paid</DialogTitle>
        <DialogContent>
          {payRow && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                <strong>{payRow.county_name}</strong> · {payRow.billing_type.replace('_', ' ')} · period {new Date(payRow.period_start).toLocaleDateString()} → {new Date(payRow.period_end).toLocaleDateString()}
                <br />
                {payRow.billing_type === 'service_fee'
                  ? <>County owes provider <strong>{fmtKES(payRow.provider_share_total)}</strong></>
                  : <>Provider owes county <strong>{fmtKES(payRow.county_share_total)}</strong></>}
              </Alert>
              <TextField label="Payout reference" required value={payRef} onChange={(e) => setPayRef(e.target.value)}
                placeholder="e.g. MPESA confirmation code, bank transaction ref" />
              <TextField label="Paid out at" type="date" InputLabelProps={{ shrink: true }} value={paidAt}
                onChange={(e) => setPaidAt(e.target.value)} />
              <TextField label="Notes (optional)" multiline minRows={2} value={payNote} onChange={(e) => setPayNote(e.target.value)} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayRow(null)}>Cancel</Button>
          <Button variant="contained" color="success" disabled={!payRef || markPaid.isPending} onClick={() => markPaid.mutate()}>
            {markPaid.isPending ? 'Saving…' : 'Mark as Paid'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};
